import { useMemo } from 'react';

import {
  BandageIcon,
  LegalDocument02Icon,
  SignatureIcon,
  UserGroup03Icon,
} from '@hugeicons/core-free-icons';
import { CandidateCheckScreens } from 'constants/candidate_screens';
import {
  buildAllFormValues,
  buildNavPath as buildNavPathHelper,
} from 'helpers/candidate_application/form-page-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { getSectionIcon } from 'helpers/iconsHelper';
import { useQueryParams } from 'hooks/query-params';
import i18n from 'i18n';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { DataCard } from 'components/Cards/DataCards/DataCard';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import SliderMotion from 'components/Motion/SliderMotion';

import { selectCandidateApplication } from 'api/candidate-application-slice';

const CandidateDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [admin] = useQueryParams(['admin']);
  const { token } = useParams();

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);
  const {
    application: { candidate, brand, candidate_checks, forms, check_values },
    candidateFields,
    fetched,
  } = useSelector(selectCandidateApplication);

  // Create a lookup map for efficient check type lookups
  const checksByType = useMemo(() => {
    if (!candidate_checks) return {};
    return candidate_checks.reduce((acc, check) => {
      acc[check.type] = check;
      return acc;
    }, {});
  }, [candidate_checks]);

  const checkStatus = (check, checkValues = {}) => {
    const rejected =
      (checkValues?.rejected && checkValues?.rejected_values?.values.length > 0) || false;
    const fixed = (checkValues?.rejected && checkValues?.rejected_fixes?.values) || false;
    const completed = check?.completed || check?.completed_at || checkValues?.completed || false;
    const submitted = check?.submitted_at || checkValues?.submitted_at || false;

    let status = {
      text: t('statuses.pending'),
      status: 'neutral',
    };

    if ((completed && !rejected) || submitted) {
      status.text = t('statuses.completed');
      status.status = 'success';
    }

    if (fixed) {
      status.text = t('statuses.fixed');
      status.status = 'success';
    }
    if (rejected && !fixed) {
      status.text = t('statuses.rejected');
      status.status = 'error';
    }

    return status;
  };

  // Prepare main check cards
  const referenceCheckCards = useMemo(() => {
    // Icon mapping to ensure fresh component references
    const iconMap = {
      reference: UserGroup03Icon,
      custom_reference: UserGroup03Icon,
    };

    const checkConfigs = [
      {
        type: 'reference',
        screen: CandidateCheckScreens.REFERENCE,
      },
      {
        type: 'custom_reference',
        screen: CandidateCheckScreens.CUSTOM_REFERENCE,
      },
    ];

    return (checkConfigs || [])
      ?.filter(config => checksByType?.[config.type])
      ?.map(config => {
        const check = checksByType?.[config?.type];
        return {
          check,
          title: t(`candidate.dashboard.${config?.type}.title`),
          description: t(`candidate.dashboard.${config?.type}.description`),
          icon: iconMap[config.type], // Use fresh icon reference from map
          disabled: !!check.submitted_at,
          status: checkStatus(check, {}),
          onClick: () =>
            navigate(buildNavPath(`/form_submission/candidate/${config?.screen}/${token}`)),
          code: config.type, // add code for DataCard key
        };
      });
  }, [checksByType, navigate, token, t]);

  // Prepare main check cards
  const checkCards = useMemo(() => {
    // Icon mapping to ensure fresh component references
    const iconMap = {
      document_signing: SignatureIcon,
      afp_backy: LegalDocument02Icon,
    };

    const checkConfigs = [
      {
        type: 'document_signing',
        screen: CandidateCheckScreens.DOCUMENT_SIGNING,
      },
      {
        type: 'afp_backy',
        screen: CandidateCheckScreens.BACKY_PORTAL,
      },
    ];

    return (checkConfigs || [])
      ?.filter(config => checksByType?.[config.type])
      ?.map(config => {
        const check = checksByType?.[config?.type];
        return {
          check,
          title: t(`candidate.dashboard.${config?.type}.title`),
          description: t(`candidate.dashboard.${config?.type}.description`),
          icon: iconMap[config.type], // Use fresh icon reference from map
          disabled: !!check.submitted_at,
          status: checkStatus(check, {}),
          onClick: () =>
            navigate(buildNavPath(`/form_submission/candidate/${config?.screen}/${token}`)),
          code: config.type, // add code for DataCard key
        };
      });
  }, [checksByType, navigate, token, t]);

  // Prepare questionnaire sub-checks
  const questionnaireSubChecks = useMemo(() => {
    const questionnaireCheck = checksByType['candidate_questionnaire'];
    if (!questionnaireCheck) return [];

    return (
      questionnaireCheck.sub_checks?.map(subCheck => ({
        check: subCheck,
        title: subCheck.title,
        description: t('candidate.dashboard.candidate_questionnaire.description'),
        icon: UserGroup03Icon,
        disabled: !!subCheck.completed_at,
        checkValues: {},
        status: checkStatus(subCheck, {}),
        onClick: () =>
          navigate(
            buildNavPath(
              `/form_submission/candidate/${CandidateCheckScreens.QUESTIONNAIRE}/${token}?subCheckId=${subCheck.id}`,
            ),
          ),
        code: `questionnaire-${subCheck.id}`,
      })) || []
    );
  }, [checksByType, navigate, token, t]);

  // Helper function to check if a section is completed
  const isSectionCompleted = (sectionCode, status) => {
    // If status is an object (checkCards, questionnaireSubChecks), check the status property
    if (typeof status === 'object' && status !== null) {
      return status?.status === 'success' || status?.status === 'completed';
    }
    // If status is a string (candidateFieldsSections), check directly
    if (typeof status === 'string') {
      return status === 'completed';
    }
    // Fallback: check system_values
    const systemStatus = check_values?.system_values?.[`${sectionCode}_status`];
    return systemStatus === 'completed';
  };

  // Compute visible subsections for all sections using the same logic as useVisibleSubsections hook
  // This must be done at the top level to avoid calling hooks in a loop
  const visibleSubsectionsBySectionCode = useMemo(() => {
    if (!candidateFields) return {};

    const { fieldPathIndex } = candidateFields;
    const allValues = buildAllFormValues({
      candidateFields,
    });

    // Reuse the same logic from useVisibleSubsections hook
    const isFieldVisible = field => {
      if (!field) return false;

      const fieldVisible = evaluateFieldConditions(field.conditions, allValues, { fieldPathIndex });
      if (!fieldVisible) return false;

      if (field?.type === 'object' || field?.type === 'id_requirements') {
        const subFields = Array.isArray(field?.subFields) ? field.subFields : [];
        if (subFields.length === 0) return false;

        const anySubVisible = subFields.some(subField =>
          evaluateFieldConditions(subField?.conditions, allValues, {
            parentCode: field?.code,
            fieldPathIndex,
          }),
        );

        return anySubVisible;
      }

      return true;
    };

    const getVisibleSubsections = subsections => {
      if (!Array.isArray(subsections) || subsections.length === 0) return [];

      return subsections.filter(subsection => {
        const fields = Array.isArray(subsection?.fields) ? subsection.fields : [];
        if (fields.length === 0) return false;

        return fields.some(isFieldVisible);
      });
    };

    // Build a map of section code to hasVisibleSubsections boolean
    const map = {};
    (candidateFields?.sections || []).forEach(section => {
      if (section?.subsections?.length > 0) {
        const visibleSubsections = getVisibleSubsections(section.subsections);
        map[section.code] = visibleSubsections.length > 0;
      }
    });

    return map;
  }, [candidateFields]);

  const hasMatchingCode = section => {
    // Collect all codes from fields + subfields
    const allCodes = section?.subsections?.flatMap(sub =>
      sub?.fields?.flatMap(field => {
        const codes = [field?.code];
        if (Array.isArray(field?.subFields)) {
          codes.push(...field?.subFields?.map(sf => sf?.code));
        }
        return codes;
      }),
    );

    // Check if any of the target codes exist in allCodes
    return (
      check_values?.rejected_values?.values?.some(code => allCodes.includes(code)) ||
      check_values?.rejected_values?.files?.some(code => allCodes.includes(code))
    );
  };

  // Combine all sections
  const allSections = useMemo(() => {
    const { candidateFieldsSections, consentsSection } = (candidateFields?.sections || []).reduce(
      (acc, section) => {
        let containsRejectedCode = false;
        if (check_values?.rejected) {
          containsRejectedCode = hasMatchingCode(section);
        }
        const updateSection = {
          ...section,
          status: containsRejectedCode
            ? 'rejected'
            : check_values?.system_values?.[`${section.code}_status`],
          hasVisibleSubsections:
            section?.subsections?.length > 0
              ? (visibleSubsectionsBySectionCode[section.code] ?? true)
              : undefined,
        };

        if (section.code === 'consents') {
          acc.consentsSection = updateSection;
        } else {
          acc.candidateFieldsSections.push(updateSection);
        }
        return acc;
      },
      { candidateFieldsSections: [], consentsSection: null },
    );

    //Remove sections with hasVisibleSubsections === false
    const filteredCandidateFieldSections = (candidateFieldsSections || [])?.filter(
      s => s.hasVisibleSubsections !== false,
    );

    // Build the full ordered list of sections
    const fullSectionList = [
      ...referenceCheckCards,
      ...filteredCandidateFieldSections,
      ...checkCards,
      ...questionnaireSubChecks,
      ...(consentsSection ? [consentsSection] : []),
    ];

    // Enable sections sequentially - each section is enabled only if all previous sections are completed
    const sectionsWithDisabledState = fullSectionList.map((section, index) => {
      // First section is always enabled
      if (index === 0 || section.status === 'rejected' || candidate?.submitted_at) {
        return {
          ...section,
          disabled: section.disabled,
        };
      }

      // Check if all previous sections are completed
      const allPreviousCompleted = fullSectionList.slice(0, index).every(prevSection => {
        const prevStatus = prevSection.status;
        const prevCode = prevSection.code;
        return isSectionCompleted(prevCode, prevStatus);
      });

      // For consents section, check if all other sections are completed
      if (section.code === 'consents') {
        const allOtherSectionsCompleted = fullSectionList
          .filter(s => s.code !== 'consents')
          .every(otherSection => {
            const otherStatus = otherSection.status;
            const otherCode = otherSection.code;
            return isSectionCompleted(otherCode, otherStatus);
          });

        return {
          ...section,
          disabled:
            !allOtherSectionsCompleted ||
            (candidate.submitted_at && !check_values.rejected) ||
            check_values?.system_values?.[`${section.code}_status`] === 'completed',
        };
      }

      // For other sections, disable if previous sections are not all completed
      return {
        ...section,
        disabled: !allPreviousCompleted,
      };
    });

    return sectionsWithDisabledState;
  }, [
    checkCards,
    referenceCheckCards,
    questionnaireSubChecks,
    candidateFields?.sections,
    check_values?.system_values,
    visibleSubsectionsBySectionCode,
  ]);

  const getStatus = status => {
    switch (status) {
      case 'success':
      case 'completed':
        return { text: t('statuses.completed'), status: 'success' };
      case 'in-progress':
        return { text: t('statuses.in-progress'), status: 'in-progress' };
      case 'rejected':
        return { text: t('statuses.requiresUpdate'), status: 'warning' };
      case 'error':
        return { text: t('statuses.error'), status: 'error' };
      default:
        return { text: t('statuses.yetToStart'), status: 'neutral' };
    }
  };

  // Early return if data not fetched yet
  if (!fetched) {
    return (
      <CandidatePage token={token} withTopLogo brand={brand}>
        <CandidatePage.View>
          <CandidatePage.Card>
            <p>{t('common.loading')}</p>
          </CandidatePage.Card>
        </CandidatePage.View>
      </CandidatePage>
    );
  }

  const navigateToSection = section => {
    if (section.index_page) {
      navigate(buildNavPath(`/form_submission/candidate/${section.code}/${token}`));
    } else {
      navigate(buildNavPath(`/form_submission/candidate/${section.code}/${token}/page/0`));
    }
  };

  return (
    <CandidatePage token={token} brand={brand} withTopLogo displayLanguageSelector>
      <CandidatePage.View>
        <CandidatePage.Card>
          <CandidatePage.Header
            brand={brand}
            showLogo={false}
            candidateName={candidate?.first_name}
            showUserDetails
            userDetails={{
              name: candidate?.first_name + ' ' + candidate?.last_name,
              email: candidate?.email,
              totalSections: (allSections || []).length,
              completedSections: (allSections || [])?.filter(
                section => section?.status?.status === 'success' || section?.status === 'completed',
              ).length,
            }}
          />
          <AnimatePresence mode="wait">
            <div className="d-flex flex-column u-gap-large">
              {check_values?.rejected && (
                <FormMessage
                  message={
                    check_values?.rejected_values?.reason || t('candidate.dashboard.verifyDetails')
                  }
                  type={MessageType.Warning}
                  closeable={false}
                  noMargin
                />
              )}

              {allSections?.length === 0 && (
                <SliderMotion key="success-application" duration={0.6} delay={0.15}>
                  <div className="d-flex flex-column u-gap-large align-items-center">
                    <img
                      src="assets/images/success-application.jpg"
                      alt="Application submitted illustration"
                      className="u-width-75 u-border-radius--half"
                    />
                    <FormMessage
                      type="Info"
                      closeable={false}
                      message={t('candidate.dashboard.candidateFormCompleted')}
                    />
                  </div>
                </SliderMotion>
              )}

              {(allSections || [])?.map((section, index) => {
                if (section?.subsections?.length > 0 && section?.hasVisibleSubsections === false) {
                  return null;
                }
                return (
                  <SliderMotion key={section.code + index} duration={0.6} delay={index * 0.15}>
                    <DataCard
                      key={section.code} // code should be unique
                      keyProp={section.code}
                      title={section.title || section.code}
                      icon={section?.icon || getSectionIcon(section.code)}
                      description={section.description}
                      background="secondary"
                      disabled={section?.disabled}
                      status={
                        typeof section?.status === 'string'
                          ? getStatus(section?.status)
                          : section?.status
                      }
                      showStatusBorder
                      onClick={() =>
                        section?.onClick ? section.onClick() : navigateToSection(section)
                      }
                    />
                  </SliderMotion>
                );
              })}
            </div>
          </AnimatePresence>
        </CandidatePage.Card>
      </CandidatePage.View>
    </CandidatePage>
  );
};

export default CandidateDashboard;
